import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import {
    FormControl,
    ReactiveFormsModule,
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    Validators
} from '@angular/forms';

import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, takeUntil, withLatestFrom } from 'rxjs/operators';

import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { isEqual } from 'lodash-es';
import { AutocompleteFunction } from '../../../../core/common/entities/autocompleteFunction.entity';
import { ShActionItemComponent } from '../../../../elements/components/action-item/action-item.component';
import {
    ActionItem,
    ActionItemModifier,
    ActionItemStyle,
    ButtonActionItem
} from '../../../../elements/components/action-item/entities/action-item.entity';
import { IconSize } from '../../../../elements/components/icon/entities/icon-sizes.entity';
import { MakeProvider } from '../../../../helpers/base-control-value-accessor';
import { isNotNil } from '../../../../helpers/is-not-nil';
import { MultiValueFormInput } from '../../../../helpers/multi-value-form-input';
import { isNotNullOrUndefined } from '../../../../helpers/typed-reactive-filters.helper';
import { FormSelectOption } from '../../../entities/form-select-option.entity';
import { FormValue, FormValueState } from '../../../entities/form-value.entity';
import { ShAutocompleteComponent } from '../../_core/autocomplete/autocomplete.component';
import { ShFormLabelComponent } from '../../_core/form-label/form-label.component';
import { ShFormSelectBaseComponent } from '../../form-select/base/form-select-base.component';
import { TypedValue } from '../typed-values.entity';

export type AbstractControlStatus = 'VALID' | 'INVALID' | 'PENDING' | 'DISABLED';

@Component({
    selector: 'sh-form-typed-value-list-complex-base',
    templateUrl: './form-typed-value-list-complex-base.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [MakeProvider(ShFormTypedValueListComplexBaseComponent)],
    standalone: true,
    imports: [
        ReactiveFormsModule,
        NgFor,
        ShFormSelectBaseComponent,
        NgIf,
        ShFormLabelComponent,
        ShAutocompleteComponent,
        ShActionItemComponent,
        AsyncPipe
    ]
})
export class ShFormTypedValueListComplexBaseComponent extends MultiValueFormInput<TypedValue> implements OnInit {
    @Input() types: FormSelectOption[] = [];
    @Input() typePlaceholderMsg = '';
    @Input() valueLabel = '';
    @Input() valuePlaceholderMsg = '';
    @Input() id!: string;
    @Input() required = false;
    @Input() indeterminateMsg = '';
    @Input() autocompleteFcts: Record<string, AutocompleteFunction> = {};
    @Input() set isDisabled(disabled: boolean) {
        this.setDisabledState(disabled);
    }

    public isIndeterminate = false;
    public emitValues = true;
    public autocompleteStreams: Observable<AutocompleteFunction | undefined>[] = [];

    private listValueChanges$!: Observable<TypedValue[]>;
    private listStatusChanges$!: Observable<AbstractControlStatus>;
    private emitValues$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

    private savedInput: FormValue<TypedValue>[] = [];

    list: UntypedFormArray;
    actionItems: ActionItem[][] = [];

    constructor(
        private readonly cd: ChangeDetectorRef,
        private readonly formBuilder: UntypedFormBuilder
    ) {
        super(cd);
        this.list = this.formBuilder.array([]);
    }

    ngOnInit(): void {
        this.listValueChanges$ = this.list.valueChanges;
        this.listStatusChanges$ = this.list.statusChanges.pipe(distinctUntilChanged(), startWith(this.list.status));

        combineLatest([this.listValueChanges$, this.listStatusChanges$])
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.calculateActionItems());

        this.listValueChanges$
            .pipe(
                distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
                withLatestFrom(this.emitValues$),
                filter(([_, emit]) => emit === true),
                map(([listValues, _]) => listValues),
                takeUntil(this.destroy$)
            )
            .subscribe(results => {
                this.emitState(results);
            });
    }

    private emitState(results: readonly TypedValue[]): void {
        const filtered: TypedValue[] = results.filter(result => result.type && result.value);
        const converted: FormValue<TypedValue>[] = filtered.map(res => new FormValue(res));
        const merged: FormValue<TypedValue>[] = this.mergeWithSaved(converted, this.savedInput);

        this.propagateChange(merged);
    }

    private mergeWithSaved(
        formValues: readonly FormValue<TypedValue>[],
        savedValues: readonly FormValue<TypedValue>[]
    ): FormValue<TypedValue>[] {
        // Assuming formValues are always SET
        const indeterminateSavedValues: FormValue<TypedValue>[] = savedValues
            .filter(savedValue => savedValue.state === FormValueState.INDETERMINATE)
            .filter(savedValue => !formValues.find(formValue => isEqual(formValue.value, savedValue.value)));

        const availableTypes: string[] = this.types.map(type => type.value);

        // Always preserve the values that are not selectable by the user
        const unavailableTypeValues: FormValue<TypedValue>[] = savedValues.filter(
            savedValue => !availableTypes.includes(savedValue.value.type)
        );

        const unsetSavedValues: FormValue<TypedValue>[] = savedValues
            .filter(savedValue => savedValue.state === FormValueState.UNSET)
            .filter(savedValue => !formValues.find(formValue => isEqual(formValue.value, savedValue.value)));

        const setSavedValues: FormValue<TypedValue>[] = savedValues
            .filter(savedValue => savedValue.state === FormValueState.SET)
            .filter(savedValue => !formValues.find(formValue => isEqual(formValue.value, savedValue.value)));

        // These are SET as saved, but not in final output. Should be added as UNSET
        const additionalUnsetValues: FormValue<TypedValue>[] = setSavedValues.map(
            formValue => new FormValue(formValue.value, FormValueState.UNSET)
        );

        return [
            ...formValues,
            ...unsetSavedValues,
            ...additionalUnsetValues,
            ...indeterminateSavedValues,
            ...unavailableTypeValues
        ];
    }

    writeValueImpl(input: readonly FormValue<TypedValue>[]): void {
        this.savedInput = [...input];
        this.emitValues$.next(false);

        const availableTypes: string[] = this.types.map(type => type.value);

        const onlySetAvailableValues: FormValue<TypedValue>[] = input.filter(
            formValue => formValue.state === FormValueState.SET && availableTypes.includes(formValue.value.type)
        );

        this.list.clear();

        onlySetAvailableValues
            .map(typedValue => this.createFormGroup(typedValue.value))
            .forEach(formGroup => {
                this.list.push(formGroup);
                this.autocompleteStreams.push(this.createAutocompleteStream(formGroup));
            });

        this.isIndeterminate = !!input.find(formValue => formValue.state === FormValueState.INDETERMINATE);

        if (this.list.length === 0) {
            this.addEmptyLine();
        }

        this.emitValues$.next(true);

        this.emitState(this.list.value);
    }

    showLabel(index: number): boolean {
        return index !== 0;
    }

    getTypeControl(index: number): UntypedFormControl {
        return this.list.at(index).get('type') as UntypedFormControl;
    }

    getValueControl(index: number): UntypedFormControl {
        return this.list.at(index).get('value') as UntypedFormControl;
    }

    getValueId(index: number): string {
        return [this.id, 'value', index].join('-');
    }

    getTypeId(index: number): string {
        return [this.id, 'type', index].join('-');
    }

    private calculateActionItems(): void {
        const actionItems: ActionItem[][] = [];
        for (let i = 0; i < this.list.length; i++) {
            actionItems[i] = this.getActionItems(i);
        }
        this.actionItems = actionItems;
    }

    private createFormGroup(item?: TypedValue): UntypedFormGroup {
        return this.formBuilder.group({
            type: this.formBuilder.control(
                item?.type ?? null,
                [this.required ? Validators.required : null].filter(isNotNil)
            ),
            value: this.formBuilder.control(
                item?.value ?? null,
                [this.required ? Validators.required : null].filter(isNotNil)
            )
        });
    }

    private addEmptyLine(): void {
        const formGroup: UntypedFormGroup = this.createFormGroup();
        this.list.push(formGroup);
        this.autocompleteStreams.push(this.createAutocompleteStream(formGroup));
    }

    private unsetIfSaved(typedValue: TypedValue): void {
        const index: number = this.savedInput.findIndex(formValue => formValue.value === typedValue);
        if (index > -1) {
            const update: FormValue<TypedValue> = { value: typedValue, state: FormValueState.UNSET };
            this.savedInput.splice(index, 1, update);
        }
    }

    private removeItem(index: number): void {
        const item: TypedValue = this.list.at(index).value;
        this.unsetIfSaved(item);
        this.list.removeAt(index);
        this.autocompleteStreams.splice(index);
    }

    private getActionItems(index: number): ActionItem[] {
        const removeAction: ButtonActionItem = {
            eventListener: this.removeItem.bind(this, index),
            type: 'button',
            style: ActionItemStyle.PureIconButton,
            icon: 'minus',
            label: '',
            iconSize: IconSize.REGULAR,
            disabled: this.getDisabled
        };

        const addAction: ButtonActionItem = {
            eventListener: this.addEmptyLine.bind(this),
            type: 'button',
            style: ActionItemStyle.PureIconButton,
            icon: 'add',
            label: '',
            iconSize: IconSize.REGULAR,
            disabled: this.getDisabled
        };

        const invisibleMixin: Partial<ButtonActionItem> = {
            modifiers: [ActionItemModifier.Invisible]
        };

        if (this.list.controls.length === 1) {
            return [addAction, { ...addAction, ...invisibleMixin }];
        }
        if (index === this.list.controls.length - 1) {
            return [removeAction, addAction];
        }
        return [removeAction, { ...addAction, ...invisibleMixin }];
    }

    private createAutocompleteStream(control: UntypedFormGroup): Observable<AutocompleteFunction | undefined> {
        const typeControl: FormControl | null = control.get('type') as FormControl;
        if (!typeControl) {
            throw new Error(`Formcontrol does not have a child control 'type'.`);
        } else {
            return typeControl.valueChanges.pipe(
                startWith(typeControl.value),
                isNotNullOrUndefined(),
                map(value => {
                    if (
                        !value ||
                        !this.autocompleteFcts ||
                        !Object.prototype.hasOwnProperty.call(this.autocompleteFcts, value)
                    ) {
                        return undefined;
                    } else {
                        return this.autocompleteFcts[value];
                    }
                })
            );
        }
    }

    private get getDisabled(): boolean {
        return this.disabled;
    }
}
